import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { fetchStaffSocketToken } from '../hooks/useChatSocket.js';
import { CHAT_SOCKET_PATH, getChatSocketUrl } from '../utils/chatSocket.js';
import { useAuth } from './AuthContext.jsx';

const StaffChatSocketContext = createContext(null);

export function StaffChatSocketProvider({ children }) {
  const { user } = useAuth();
  const enabled = Boolean(user?.capabilities?.live_chat);

  const socketRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const inboxListenersRef = useRef(new Set());

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [error, setError] = useState('');

  const appendMessage = useCallback((msg) => {
    if (!msg?.id) return;
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  const notifyInbox = useCallback((payload) => {
    inboxListenersRef.current.forEach((fn) => fn(payload));
    window.dispatchEvent(new CustomEvent('staff-chat-inbox', { detail: payload }));
  }, []);

  const emitJoin = useCallback((conversationId) => {
    const socket = socketRef.current;
    if (!socket?.connected || !conversationId) return;

    socket.emit('chat:join', { conversation_id: conversationId }, (res) => {
      if (!res?.ok) {
        setError(res?.error || 'Failed to join chat');
        return;
      }
      setError('');
      if (res.conversation) setConversation(res.conversation);
      if (Array.isArray(res.messages)) setMessages(res.messages);
    });
  }, []);

  const joinConversation = useCallback(
    (conversationId) => {
      activeConversationIdRef.current = conversationId || null;
      if (!conversationId) {
        setMessages([]);
        setConversation(null);
        return;
      }
      emitJoin(conversationId);
    },
    [emitJoin],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    (async () => {
      setConnecting(true);
      setError('');
      try {
        const { token } = await fetchStaffSocketToken();
        if (cancelled) return;

        const socket = io(getChatSocketUrl(), {
          path: CHAT_SOCKET_PATH,
          auth: { token },
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionAttempts: Infinity,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          setConnected(true);
          setConnecting(false);
          setError('');
          if (activeConversationIdRef.current) {
            emitJoin(activeConversationIdRef.current);
          }
        });
        socket.on('disconnect', () => setConnected(false));
        socket.on('connect_error', (err) => {
          setConnecting(false);
          setConnected(false);
          setError(err.message || 'Chat connection failed');
        });
        socket.on('chat:message', (msg) => {
          if (
            activeConversationIdRef.current &&
            msg?.conversation_id === activeConversationIdRef.current
          ) {
            appendMessage(msg);
          }
        });
        socket.on('chat:conversation', (c) => {
          if (c?.id === activeConversationIdRef.current) {
            setConversation(c);
          }
        });
        socket.on('chat:inbox', notifyInbox);
      } catch (e) {
        if (!cancelled) {
          setConnecting(false);
          setError(e.message || 'Chat connection failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      setConnecting(false);
    };
  }, [enabled, appendMessage, emitJoin, notifyInbox]);

  const sendMessage = useCallback(
    (body) =>
      new Promise((resolve, reject) => {
        const socket = socketRef.current;
        const conversationId = activeConversationIdRef.current;
        if (!socket?.connected || !conversationId) {
          reject(new Error('Not connected'));
          return;
        }
        socket.emit('chat:message', { conversation_id: conversationId, body }, (res) => {
          if (res?.ok) resolve(res.message);
          else reject(new Error(res?.error || 'Send failed'));
        });
      }),
    [],
  );

  const subscribeInbox = useCallback((fn) => {
    inboxListenersRef.current.add(fn);
    return () => inboxListenersRef.current.delete(fn);
  }, []);

  const value = {
    connected,
    connecting,
    error,
    messages,
    conversation,
    appendMessage,
    sendMessage,
    joinConversation,
    subscribeInbox,
  };

  return <StaffChatSocketContext.Provider value={value}>{children}</StaffChatSocketContext.Provider>;
}

export function useStaffChatSocket() {
  const ctx = useContext(StaffChatSocketContext);
  if (!ctx) {
    throw new Error('useStaffChatSocket must be used within StaffChatSocketProvider');
  }
  return ctx;
}
